/**
 * DocStatusPill — canonical documentation status color config and reusable pill component.
 *
 * Single source of truth for documentation workflow status badge styling.
 * Covers: DRAFT, SUBMITTED, QA_REVIEW/QA, COMPLETE/CURRENT, EXPIRED.
 *
 * Usage (component):
 *   <DocStatusPill status="DRAFT" />
 *   <DocStatusPill status="QA" className="ml-2" />
 *
 * Usage (utilities):
 *   getDocStatusConfig('SUBMITTED')          → full config object
 *   getDocStatusPillClassName('COMPLETE')    → Tailwind class string
 *
 * Color scheme (light pastel bg + colored text + colored border):
 *   Draft       → stone-50  / stone-600  / stone-200
 *   Submitted   → blue-50   / blue-700   / blue-200
 *   QA Review   → amber-50  / amber-700  / amber-300
 *   Complete    → emerald-50/ emerald-800 / emerald-300
 *   Expired     → red-50    / red-700    / red-300
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface DocStatusConfig {
  /** Display label (e.g. "Draft", "QA Review") */
  label: string;
  /** Full Tailwind class string for the pill element */
  className: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

const DOC_STATUS_MAP: Record<string, DocStatusConfig> = {
  DRAFT:       { label: 'Draft',       className: `bg-stone-50 text-stone-600 border-stone-200 ${BASE}` },
  SUBMITTED:   { label: 'Submitted',   className: `bg-blue-50 text-blue-700 border-blue-200 ${BASE}` },
  QA_REVIEW:   { label: 'QA Review',   className: `bg-amber-50 text-amber-700 border-amber-300 ${BASE}` },
  QA:          { label: 'QA Review',   className: `bg-amber-50 text-amber-700 border-amber-300 ${BASE}` },
  COMPLETE:    { label: 'Complete',    className: `bg-emerald-50 text-emerald-800 border-emerald-300 ${BASE}` },
  CURRENT:     { label: 'Current',     className: `bg-emerald-50 text-emerald-800 border-emerald-300 ${BASE}` },
  EXPIRED:     { label: 'Expired',     className: `bg-red-50 text-red-700 border-red-300 ${BASE}` },
};

const DEFAULT_CONFIG: DocStatusConfig = {
  label: '',
  className: `bg-stone-50 text-stone-600 border-stone-200 ${BASE}`,
};

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a documentation status string.
 * Normalises to uppercase with underscores; falls back to a stone default.
 */
export function getDocStatusConfig(status: string): DocStatusConfig {
  if (!status) return { ...DEFAULT_CONFIG, label: status };
  const key = status.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return DOC_STATUS_MAP[key] ?? { ...DEFAULT_CONFIG, label: status };
}

/**
 * Returns the full Tailwind class string for a documentation status pill.
 */
export function getDocStatusPillClassName(status: string): string {
  return getDocStatusConfig(status).className;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface DocStatusPillProps {
  /** Documentation status: DRAFT | SUBMITTED | QA | QA_REVIEW | COMPLETE | CURRENT | EXPIRED */
  status: string;
  /** Override display label */
  label?: string;
  /** Extra Tailwind classes */
  className?: string;
}

/**
 * DocStatusPill — canonical documentation status badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + light pastel bg
 * with colored text and matching border.
 */
export function DocStatusPill({ status, label, className = '' }: DocStatusPillProps) {
  const config = getDocStatusConfig(status);
  const displayLabel = label ?? config.label;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 ${config.className} ${className}`.trim()}>
      {displayLabel}
    </span>
  );
}

export default DocStatusPill;
